import React from 'react';
import styled, { css } from 'styled-components/macro';
import translate from '../../../components/TranslateHOC';
import { device } from '../../Theme';
import { Link } from 'react-router-dom';
import SkipToLink from '../navigation/skipToLink/SkipToLink';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { Toggle as ThemeToggle } from '../toggle/Toggle';
import { ReactComponent as LightIcon } from '../../../img/icons/theme_light_icon.svg';
import { ReactComponent as DarkIcon } from '../../../img/icons/theme_dark_icon.svg';

function GlobalNav({ auth, strings, location, changeLang, scrollNavHidden }) {
  const menuItems = strings?.globalMenu || [];

  return (
    <GlobalNavHeader role="banner" scrollNavHidden={scrollNavHidden}>
      <SkipToLink />
      <GlobalNavContainer>
        <GlobalNavLeft>
          <GlobalNavNav>
            <GlobalNavList>
              {menuItems.map((item, i) => {
                const isSelected =
                  !item.isExternal && location.pathname === item.url;
                return (
                  <GlobalNavListItem key={i} isSelected={isSelected}>
                    {item.isExternal ? (
                      <a href={item.url} rel="noopener noreferrer">
                        <IconContainer colour={item.colour}>
                          <Icon materialLabel={item.icon} type="material" />
                        </IconContainer>
                        <Label>{item.title}</Label>
                      </a>
                    ) : (
                      <Link to={item.url}>
                        <IconContainer colour={item.colour}>
                          <Icon materialLabel={item.icon} type="material" />
                        </IconContainer>
                        <Label>{item.title}</Label>
                      </Link>
                    )}
                  </GlobalNavListItem>
                );
              })}
            </GlobalNavList>
          </GlobalNavNav>
        </GlobalNavLeft>
        <GlobalNavRight>
          <LangButton>
            <Button
              label={strings ? strings.langTextSwitch : 'Cymraeg'}
              onClick={() => changeLang()}
              size="small"
              type="secondary"
            />
          </LangButton>
          <Toggle auth={auth}>
            <ThemeToggle
              auth={auth}
              defaultLabel={strings.light}
              defaultIcon={<LightIcon />}
              toggledLabel={strings.dark}
              toggledIcon={<DarkIcon />}
            />
          </Toggle>
        </GlobalNavRight>
      </GlobalNavContainer>
    </GlobalNavHeader>
  );
}

const GlobalNavHeader = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.globalNavBgLight
      : props.theme.color.globalNavBgDark};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  align-items: center;
  z-index: 1;
  @media ${device.desktop} {
    overflow-x: hidden;
  }
  ${(props) =>
    props.scrollNavHidden &&
    `
      position: absolute;
      opacity: 0;
      pointer-events: none;
  `};
`;

const GlobalNavContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 18px 14px;
  @media ${device.desktop} {
    padding: 22px 32px 22px 13px;
  }
`;

const GlobalNavLeft = styled.div`
  display: flex;
  align-items: center;
`;

const LangButton = styled.div`
  display: none;
  @media screen and (min-width: 1024px) {
    display: flex;
  }
`;

const GlobalNavRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
`;

const Toggle = styled.div`
  display: none;
  ${(props) =>
    props.auth &&
    css`
      @media screen and (min-width: 768px) and (max-width: 1023px) {
        display: none;
      }
      @media screen and (min-width: 1024px) {
        display: flex;
      }
    `};

  ${(props) =>
    !props.auth &&
    css`
      display: none;
      @media screen and (min-width: 768px) {
        display: flex;
      }
    `};

  align-items: center;
  margin-left: 12px;
`;

const GlobalNavNav = styled.nav`
  display: flex;
  align-items: center;
  position: relative;
  transition: margin-left 0.3s ease-in-out;
`;

const GlobalNavList = styled.ul`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const IconContainer = styled.span`
  padding-left: 8px;
  font-size: 24px;
  position: relative;
  transform: translatex(-4px);
  transition: 0.15s;
  z-index: 1;
  opacity: 0.75;

  svg {
    width: 24px;
    height: 24px;
  }

  span {
    ${(props) => {
      const iconColourCapitalised =
        props.colour.charAt(0).toUpperCase() +
        props.colour.slice(1).toLowerCase();
      const themeNameCapitalised =
        props.theme.name.charAt(0).toUpperCase() +
        props.theme.name.slice(1).toLowerCase();
      const iconColour =
        props.theme.color[
          `globalNav` + iconColourCapitalised + themeNameCapitalised
        ];
      if (iconColour) {
        return `
          background-color: ${iconColour};
        `;
      }
      return '';
    }};
  }
`;

const Label = styled.span`
  margin: 0 4px;
  position: relative;
  z-index: 10;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.globalNavBlack75Light
      : props.theme.color.globalNavBlack75Dark};
`;

const GlobalNavListItem = styled.li`
  display: flex;
  flex-shrink: 0;
  a {
    ${(props) => props.theme.fontBold};
    align-items: center;
    background: none;
    border-radius: 2px;
    display: inline-flex;
    font-size: 20px;
    font-weight: bold;
    line-height: 24px;
    letter-spacing: -0.35px;
    justify-content: center;
    padding: 4px 8px;
    position: relative;
    text-decoration: none;
    flex: 0 0 auto;

    &:after {
      border-radius: 2px;
      bottom: -1px;
      content: '';
      display: block;
      left: 2px;
      position: absolute;
      right: -1px;
      top: -1px;
      width: calc(100% - 4px);
    }
    &:hover ${Label}, &:active ${Label} {
      ${(props) => props.theme.fontBold}
      font-weight: bold;
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.globalNavBlackFullLight
          : props.theme.color.globalNavBlackFullDark};
    }
    &:hover ${Label} {
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-thickness: 1px;
    }
    &:hover ${IconContainer}, &:active ${IconContainer} {
      opacity: 1;
    }
    &:focus-visible {
      outline: none;
      &:after {
        box-shadow: 0 0 0 2px
          ${(props) =>
            props.theme.name === 'light'
              ? props.theme.color.globalNavTealLight
              : props.theme.color.globalNavTealDark};
      }
      ${Label} {
        color: ${(props) =>
          props.theme.name === 'light'
            ? props.theme.color.globalNavBlackFullLight
            : props.theme.color.globalNavBlackFullDark};
      }
      ${IconContainer} {
        opacity: 1;
      }
    }
    &:disabled,
    &[disabled] {
      ${Label} {
        color: ${(props) => props.theme.color.globalNavBlack50};
      }
      ${IconContainer} {
        opacity: 0.5;
      }
    }
  }
  ${(props) =>
    props.isSelected &&
    css`
      a {
        ${Label} {
          color: ${props.theme.name === 'light'
            ? props.theme.color.globalNavBlackFullLight
            : props.theme.color.globalNavBlackFullDark};
        }
        ${IconContainer} {
          opacity: 1;
        }
      }
    `}
`;

export default translate(GlobalNav);
